import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Selection } from 'react-aria-components';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { CategoryTable } from './components';
import type { Category } from './components/categoriesMeta';

type CategoriesPageViewProps = {
    data: Category[];
    expandedKeys: Selection;
    onAddCategory: () => void;
    onDeleteCategory: (id: string) => void;
    onEditCategory: (id: string) => void;
    onExpandedChange: (keys: Selection) => void;
    onManageAttributes: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onSearchChange: (value: string) => void;
    page: number;
    pageSize: number;
    pageWindow: (number | 'ellipsis')[];
    search: string;
    totalCount: number;
    totalPages: number;
};

export const CategoriesPageView: React.FC<CategoriesPageViewProps> = ({
    data,
    expandedKeys,
    onAddCategory,
    onDeleteCategory,
    onEditCategory,
    onExpandedChange,
    onManageAttributes,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    page,
    pageSize,
    pageWindow,
    search,
    totalCount,
    totalPages,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            searchPlaceholder: t(
                'categories.components.categoryTable.searchPlaceholder',
            ),
            searchAria: t('categories.components.categoryTable.searchAria'),
            addCategory: t('categories.components.categoryTable.addCategory'),
            tableAria: t('categories.components.categoryTable.tableAria'),
        }),
        [t],
    );

    return (
        <section
            aria-label={copy.tableAria}
            {...getNodeTestIdProp('category-table-section')}
        >
            <div
                className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                {...getNodeTestIdProp('category-table-toolbar')}
            >
                <label
                    className="flex w-full items-center gap-2 rounded-xl border border-border bg-surface-secondary px-3 py-2 sm:w-auto sm:shrink-0"
                    htmlFor="category-table-search"
                    {...getNodeTestIdProp('category-table-search-wrap')}
                >
                    <Icon
                        aria-hidden
                        className="size-4 text-muted"
                        icon="gravity-ui:magnifier"
                    />
                    <input
                        aria-label={copy.searchAria}
                        className="w-full bg-transparent text-sm text-foreground placeholder:text-muted focus:outline-none sm:w-56"
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder={copy.searchPlaceholder}
                        type="text"
                        id="category-table-search"
                        value={search}
                        {...getNodeTestIdProp('category-table-search')}
                    />
                </label>
                <Button
                    onPress={onAddCategory}
                    variant="primary"
                    {...getNodeTestIdProp('category-table-add')}
                >
                    <Icon icon="gravity-ui:plus" />
                    {copy.addCategory}
                </Button>
            </div>
            <CategoryTable
                data={data}
                expandedKeys={expandedKeys}
                onDeleteCategory={onDeleteCategory}
                onEditCategory={onEditCategory}
                onExpandedChange={onExpandedChange}
                onManageAttributes={onManageAttributes}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                page={page}
                pageSize={pageSize}
                pageWindow={pageWindow}
                totalCount={totalCount}
                totalPages={totalPages}
            />
        </section>
    );
};
